/**
 * Make a WhatsApp template here instead of in WhatsApp Manager.
 *
 * The form follows Meta's rules — a lowercase name, variables {{1}}, {{2}} in
 * order with an example of each, at most three quick replies… — and the server
 * checks them again before Meta is asked (`whatsapp_templates.py`). A media
 * header needs a sample for Meta's review, and the same file becomes the
 * template's header in Connect. Meta decides: the template arrives *pending*,
 * and a sync reads its decision.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ImageIcon, Minus, Plus } from 'lucide-react';
import { ApiError, Alert, Button, Input, Modal, Select, Switch, Textarea, api, toast } from '@connect/web-shared';

import type { ClientChannel } from './types';

type Category = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
type HeaderFormat = 'none' | 'text' | 'image' | 'video' | 'document' | 'location';
type ButtonType = 'quick_reply' | 'url' | 'phone_number' | 'copy_code';

interface TemplateButton {
  type: ButtonType;
  text: string;
  url?: string;
  url_example?: string;
  phone_number?: string;
  example?: string;
}

const LANGUAGES = [
  ['en', 'English'],
  ['en_US', 'English (US)'],
  ['en_GB', 'English (UK)'],
  ['hi', 'Hindi'],
  ['te', 'Telugu'],
  ['ta', 'Tamil'],
  ['kn', 'Kannada'],
  ['ml', 'Malayalam'],
  ['mr', 'Marathi'],
  ['gu', 'Gujarati'],
  ['bn', 'Bengali'],
  ['pa', 'Punjabi'],
] as const;

const ACCEPT: Partial<Record<HeaderFormat, string>> = {
  image: 'image/jpeg,image/png',
  video: 'video/mp4,video/3gpp',
  document: 'application/pdf',
};

/** The variables a text uses, as Meta numbers them: each once, in order. */
export function variablesIn(text: string): number[] {
  const seen: number[] = [];
  for (const match of text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    const n = Number(match[1]);
    if (!seen.includes(n)) seen.push(n);
  }
  return seen;
}

export function WhatsAppTemplateDialog({
  channel,
  open,
  onClose,
  onMade,
}: {
  channel: ClientChannel;
  open: boolean;
  onClose: () => void;
  onMade: () => void;
}) {
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en');
  const [category, setCategory] = useState<Category>('UTILITY');
  const [headerFormat, setHeaderFormat] = useState<HeaderFormat>('none');
  const [headerText, setHeaderText] = useState('');
  const [headerExample, setHeaderExample] = useState('');
  const [sample, setSample] = useState<File | null>(null);
  const [body, setBody] = useState('');
  const [examples, setExamples] = useState<string[]>([]);
  const [footer, setFooter] = useState('');
  const [buttons, setButtons] = useState<TemplateButton[]>([]);
  const [security, setSecurity] = useState(true);
  const [expiry, setExpiry] = useState('10');
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setLanguage('en');
    setCategory('UTILITY');
    setHeaderFormat('none');
    setHeaderText('');
    setHeaderExample('');
    setSample(null);
    setBody('');
    setExamples([]);
    setFooter('');
    setButtons([]);
    setSecurity(true);
    setExpiry('10');
  }, [open]);

  const bodyVariables = useMemo(() => variablesIn(body), [body]);
  useEffect(() => {
    setExamples((current) => bodyVariables.map((_, index) => current[index] ?? ''));
  }, [bodyVariables]);

  const auth = category === 'AUTHENTICATION';
  const problem = useMemo((): string | null => {
    if (!/^[a-z0-9_]+$/.test(name)) return 'The name is lowercase letters, digits and underscores: order_update.';
    if (auth) return null;
    if (!body.trim()) return 'Write the message.';
    if (bodyVariables.some((n, index) => n !== index + 1)) return 'Number the variables {{1}}, {{2}}, … in order.';
    if (examples.some((e) => !e.trim())) return 'Give an example for each variable.';
    if (headerFormat === 'text' && !headerText.trim()) return 'Write the heading.';
    if (headerFormat === 'text' && variablesIn(headerText).length && !headerExample.trim())
      return 'Give an example for the heading’s variable.';
    if (ACCEPT[headerFormat] && !sample) return `Choose the ${headerFormat} Meta reviews it with.`;
    for (const button of buttons) {
      if (button.type !== 'copy_code' && !button.text.trim()) return 'Every button needs its text.';
      if (button.type === 'url' && !button.url?.startsWith('https://')) return 'A link starts with https://.';
      if (button.type === 'url' && variablesIn(button.url ?? '').length && !button.url_example)
        return 'Give an example of the full link.';
      if (button.type === 'phone_number' && !/^\+?\d{8,15}$/.test(button.phone_number ?? ''))
        return 'A call button needs a phone number with its country code.';
      if (button.type === 'copy_code' && !button.example) return 'Give an example code.';
    }
    return null;
  }, [name, auth, body, bodyVariables, examples, headerFormat, headerText, headerExample, sample, buttons]);

  const submit = useMutation({
    mutationFn: () => {
      const definition = auth
        ? {
            name,
            language,
            category,
            add_security_recommendation: security,
            code_expiration_minutes: Number(expiry) || null,
          }
        : {
            name,
            language,
            category,
            header_format: headerFormat,
            header_text: headerFormat === 'text' ? headerText : null,
            header_example: headerFormat === 'text' ? headerExample || null : null,
            body,
            body_examples: examples,
            footer: footer || null,
            buttons,
          };
      const form = new FormData();
      form.append('definition', JSON.stringify(definition));
      if (sample && ACCEPT[headerFormat]) form.append('sample', sample, sample.name);
      return api.upload<{ status: string }>(`/messaging/channels/${channel.public_id}/provider-templates`, form);
    },
    onSuccess: (result) => {
      toast.success('Submitted to WhatsApp', `${name} is ${result.status}: Meta reviews it, usually within minutes. Sync to see its decision.`);
      onMade();
      onClose();
    },
  });

  const setButton = (index: number, patch: Partial<TemplateButton>) =>
    setButtons(buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  const preview = body.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n: string) => examples[Number(n) - 1] || `{{${n}}}`);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New WhatsApp template"
      description="Made here, reviewed by Meta. It can be sent once Meta approves it."
      size="xl"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={problem !== null} loading={submit.isPending} onClick={() => submit.mutate()}>
            Submit to WhatsApp
          </Button>
        </>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-3">
          {submit.error ? (
            <Alert tone="danger" title="Not submitted">
              {submit.error instanceof ApiError ? submit.error.message : 'Could not reach the server.'}
            </Alert>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label="Name"
              required
              value={name}
              maxLength={512}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="order_shipped"
            />
            <Select
              label="Language"
              options={LANGUAGES.map(([value, label]) => ({ value, label }))}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            />
            <Select
              label="Category"
              options={[
                { value: 'UTILITY', label: 'Utility — about something they asked for' },
                { value: 'MARKETING', label: 'Marketing — offers and news' },
                { value: 'AUTHENTICATION', label: 'Authentication — a one-time code' },
              ]}
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
            />
          </div>

          {auth ? (
            <div className="space-y-3 rounded-control border border-line p-3">
              <p className="text-xs text-muted">
                Meta writes an authentication template itself: “<b>123456</b> is your verification code.” with a Copy code button.
              </p>
              <Switch label="Add “For your security, do not share this code.”" checked={security} onChange={(e) => setSecurity(e.target.checked)} />
              <Input label="The code expires after (minutes)" inputMode="numeric" value={expiry} onChange={(e) => setExpiry(e.target.value.replace(/\D/g, ''))} />
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
                <Select
                  label="Header"
                  options={[
                    { value: 'none', label: 'None' },
                    { value: 'text', label: 'Text' },
                    { value: 'image', label: 'Image' },
                    { value: 'video', label: 'Video' },
                    { value: 'document', label: 'Document (PDF)' },
                    { value: 'location', label: 'Location' },
                  ]}
                  value={headerFormat}
                  onChange={(e) => {
                    setHeaderFormat(e.target.value as HeaderFormat);
                    setSample(null);
                  }}
                />
                {headerFormat === 'text' ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input label="Heading" maxLength={60} value={headerText} onChange={(e) => setHeaderText(e.target.value)} placeholder="Order {{1}}" />
                    {variablesIn(headerText).length ? (
                      <Input label="Example for {{1}}" value={headerExample} onChange={(e) => setHeaderExample(e.target.value)} />
                    ) : null}
                  </div>
                ) : ACCEPT[headerFormat] ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted">Sample for Meta’s review</p>
                    <input
                      ref={fileInput}
                      type="file"
                      accept={ACCEPT[headerFormat]}
                      className="hidden"
                      onChange={(e) => setSample(e.target.files?.[0] ?? null)}
                    />
                    <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
                      <ImageIcon className="size-3.5" />
                      {sample ? sample.name : `Choose the ${headerFormat}`}
                    </Button>
                    <p className="text-2xs text-faint">It also becomes the template’s header in Connect.</p>
                  </div>
                ) : headerFormat === 'location' ? (
                  <p className="self-end text-2xs text-faint">The place is set on the template in Connect once it is approved.</p>
                ) : null}
              </div>

              <Textarea
                label="Message"
                required
                rows={4}
                maxLength={1024}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                hint="Variables are {{1}}, {{2}}… in order. *bold*, _italic_ and ~strike~ as in WhatsApp."
              />
              {bodyVariables.length ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  {bodyVariables.map((n, index) => (
                    <Input
                      key={n}
                      label={`Example for {{${n}}}`}
                      value={examples[index] ?? ''}
                      onChange={(e) => setExamples(examples.map((v, i) => (i === index ? e.target.value : v)))}
                    />
                  ))}
                </div>
              ) : null}
              <Input label="Footer (optional)" maxLength={60} value={footer} onChange={(e) => setFooter(e.target.value)} />

              <div className="space-y-2">
                <p className="text-xs font-medium text-muted">Buttons (optional)</p>
                {buttons.map((button, index) => (
                  // Buttons are edited in place and have no identity but their place.
                  // oxlint-disable-next-line react/no-array-index-key
                  <div key={index} className="grid gap-2 rounded-control border border-line p-2 sm:grid-cols-[9rem_1fr_1fr_auto] sm:items-end">
                    <Select
                      label="Kind"
                      options={[
                        { value: 'quick_reply', label: 'Quick reply' },
                        { value: 'url', label: 'Link' },
                        { value: 'phone_number', label: 'Call' },
                        { value: 'copy_code', label: 'Copy code' },
                      ]}
                      value={button.type}
                      onChange={(e) => setButton(index, { type: e.target.value as ButtonType })}
                    />
                    {button.type !== 'copy_code' ? (
                      <Input label="Text" maxLength={25} value={button.text} onChange={(e) => setButton(index, { text: e.target.value })} />
                    ) : (
                      <Input label="Example code" maxLength={15} value={button.example ?? ''} onChange={(e) => setButton(index, { example: e.target.value })} />
                    )}
                    {button.type === 'url' ? (
                      <div className="grid gap-1">
                        <Input label="Link" value={button.url ?? ''} onChange={(e) => setButton(index, { url: e.target.value })} placeholder="https://shop.example/t/{{1}}" />
                        {variablesIn(button.url ?? '').length ? (
                          <Input label="Example link" value={button.url_example ?? ''} onChange={(e) => setButton(index, { url_example: e.target.value })} />
                        ) : null}
                      </div>
                    ) : button.type === 'phone_number' ? (
                      <Input label="Phone number" value={button.phone_number ?? ''} onChange={(e) => setButton(index, { phone_number: e.target.value })} placeholder="+919876543210" />
                    ) : (
                      <span />
                    )}
                    <Button variant="ghost" size="sm" aria-label={`Remove button ${index + 1}`} onClick={() => setButtons(buttons.filter((_, i) => i !== index))}>
                      <Minus className="size-3.5" />
                    </Button>
                  </div>
                ))}
                {buttons.length < 10 ? (
                  <Button variant="secondary" size="sm" onClick={() => setButtons([...buttons, { type: 'quick_reply', text: '' }])}>
                    <Plus className="size-3.5" />
                    Add a button
                  </Button>
                ) : null}
              </div>
            </>
          )}
          {problem && name ? <p className="text-xs text-muted">{problem}</p> : null}
        </div>

        <div className="space-y-1">
          <p className="text-xs font-medium text-muted">Preview</p>
          <div className="rounded-lg bg-[#e5ddd5] p-3 dark:bg-surface-2">
            <div className="max-w-full space-y-1 rounded-lg bg-white p-2 text-xs text-[#111b21] shadow-sm dark:bg-surface dark:text-body">
              {auth ? (
                <>
                  <p>
                    <b>123456</b> is your verification code.{security ? ' For your security, do not share this code.' : ''}
                  </p>
                  {expiry ? <p className="text-2xs text-[#667781]">This code expires in {expiry} minutes.</p> : null}
                  <p className="border-t pt-1 text-center font-medium text-[#00a5f4]">Copy code</p>
                </>
              ) : (
                <>
                  {headerFormat === 'text' && headerText ? (
                    <p className="font-semibold">{headerText.replace(/\{\{\s*1\s*\}\}/, headerExample || '{{1}}')}</p>
                  ) : ACCEPT[headerFormat] || headerFormat === 'location' ? (
                    <div className="grid h-24 place-items-center rounded bg-[#d1d7db] text-2xs text-[#667781]">
                      {headerFormat === 'location' ? 'Map' : sample?.name ?? headerFormat}
                    </div>
                  ) : null}
                  <p className="whitespace-pre-wrap">{preview || 'Your message'}</p>
                  {footer ? <p className="text-2xs text-[#667781]">{footer}</p> : null}
                  {buttons.map((button, index) => (
                    // oxlint-disable-next-line react/no-array-index-key
                    <p key={index} className="border-t pt-1 text-center font-medium text-[#00a5f4]">
                      {button.type === 'copy_code' ? 'Copy offer code' : button.text || '…'}
                    </p>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
